export interface StageTouchGestureOptions {
  stage: HTMLElement;
  getZoom: () => number;
  setZoom: (zoom: number) => void;
  clampZoom: (zoom: number) => number;
  /** Called when a 2-finger gesture begins (cancel in-progress draw tools). */
  onGestureStart?: () => void;
  /** Called when the gesture fully ends (no fingers left in a pinch/pan). */
  onGestureEnd?: () => void;
  /** Single-finger double-tap (e.g. zoom to fit / 100%). */
  onDoubleTap?: (clientX: number, clientY: number) => void;
}

type GestureSession = {
  startDist: number;
  startZoom: number;
  lastMidX: number;
  lastMidY: number;
};

function touchDistance(a: Touch, b: Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function touchMid(a: Touch, b: Touch): { x: number; y: number } {
  return {
    x: (a.clientX + b.clientX) / 2,
    y: (a.clientY + b.clientY) / 2,
  };
}

/**
 * Pinch-to-zoom + two-finger pan on the editor pasteboard stage.
 * Uses CSS zoom percentage (same as Ctrl+wheel) and stage scroll for pan.
 */
export function installStageTouchGestures(
  options: StageTouchGestureOptions
): () => void {
  const {
    stage,
    getZoom,
    setZoom,
    clampZoom,
    onGestureStart,
    onGestureEnd,
    onDoubleTap,
  } = options;

  let session: GestureSession | null = null;
  let active = false;
  let lastTapAt = 0;
  let lastTapX = 0;
  let lastTapY = 0;
  let multiTouchSeen = false;

  const begin = (e: TouchEvent) => {
    if (e.touches.length < 2) return;
    const a = e.touches[0];
    const b = e.touches[1];
    const dist = touchDistance(a, b);
    if (dist < 8) return;
    const mid = touchMid(a, b);
    session = {
      startDist: dist,
      startZoom: getZoom(),
      lastMidX: mid.x,
      lastMidY: mid.y,
    };
    if (!active) {
      active = true;
      stage.classList.add('ps-stage-gesturing');
      onGestureStart?.();
    }
    e.preventDefault();
  };

  const onTouchStart = (e: TouchEvent) => {
    if (e.touches.length >= 2) {
      multiTouchSeen = true;
      begin(e);
    }
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!session || e.touches.length < 2) return;
    e.preventDefault();

    const a = e.touches[0];
    const b = e.touches[1];
    const dist = Math.max(8, touchDistance(a, b));
    const mid = touchMid(a, b);

    const nextZoom = clampZoom(session.startZoom * (dist / session.startDist));
    if (Math.abs(nextZoom - getZoom()) >= 1) {
      setZoom(nextZoom);
    }

    const dx = mid.x - session.lastMidX;
    const dy = mid.y - session.lastMidY;
    if (dx !== 0 || dy !== 0) {
      stage.scrollLeft -= dx;
      stage.scrollTop -= dy;
      session.lastMidX = mid.x;
      session.lastMidY = mid.y;
    }
  };

  const endIfNeeded = (e: TouchEvent) => {
    if (e.touches.length >= 2) {
      // Re-anchor so zoom doesn't jump when one finger lifts then another stays
      begin(e);
      return;
    }
    if (session) {
      session = null;
    }
    if (active && e.touches.length === 0) {
      active = false;
      stage.classList.remove('ps-stage-gesturing');
      onGestureEnd?.();
      multiTouchSeen = false;
      return;
    }

    // Double-tap (single finger only — ignore if this touch sequence included a pinch)
    if (
      onDoubleTap &&
      !multiTouchSeen &&
      e.touches.length === 0 &&
      e.changedTouches.length === 1
    ) {
      const t = e.changedTouches[0];
      const now = Date.now();
      const near =
        Math.hypot(t.clientX - lastTapX, t.clientY - lastTapY) < 36;
      if (now - lastTapAt < 320 && near) {
        onDoubleTap(t.clientX, t.clientY);
        lastTapAt = 0;
      } else {
        lastTapAt = now;
        lastTapX = t.clientX;
        lastTapY = t.clientY;
      }
    }

    if (e.touches.length === 0) {
      multiTouchSeen = false;
    }
  };

  stage.addEventListener('touchstart', onTouchStart, { passive: false });
  stage.addEventListener('touchmove', onTouchMove, { passive: false });
  stage.addEventListener('touchend', endIfNeeded, { passive: false });
  stage.addEventListener('touchcancel', endIfNeeded, { passive: false });

  return () => {
    stage.removeEventListener('touchstart', onTouchStart);
    stage.removeEventListener('touchmove', onTouchMove);
    stage.removeEventListener('touchend', endIfNeeded);
    stage.removeEventListener('touchcancel', endIfNeeded);
    stage.classList.remove('ps-stage-gesturing');
    session = null;
    active = false;
  };
}
